import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Row, Col } from 'reactstrap';

import Page from '../shared/Page';
import SaveButton from '../shared/SaveButton';
import CancelButton from '../shared/CancelButton';
import withRoleRestrictions from '../shared/withRoleRestrictions';
import CompanyDropdown from '../shared/CompanyDropdown';
import Loader from '../shared/Loader';
import { STANDARD_TEMPLATE } from '../../constants/reportLayouts';
import { ADMINS } from '../../constants/userRoles';
import { DASHBOARD, PAGE_PATHS } from '../../constants/pathsRoots';
import { notifyError, notifyInfo } from '../../utils/notifier';
import { getBreadCrumbs } from '../../utils/breadCrumbs';
import withEulaRestrictions from '../license/withEulaRestrictions';
import { fetchReportOptions, updateReportOptions } from '../../actions/companies/apiRequests';
import { fetchAllVisibleLayouts } from '../../actions/layout/apiRequests';

import ReportOptions from './ReportOptions';
import ChartReferenceLine from './ChartReferenceLine';
import ReportLayouts from './ReportLayouts';
import OfficialReports from './OfficialReports';
import TemplateOptions from './TemplateOptions';
import { deserializeDefaultLayout, getDefaultReportLayoutOptions, serializeDefaultLayout } from './utils';
import UncertaintyCalculation from './UncertaintyCalculation';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.js';

class ReportManagementPage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      companyId: null,
      highLevel: 0,
      lowLevel: 0,
      chartReferenceLine: null,
      customLabel: null,
      customLevel: null,
      defaultLayout: null,
      layoutTemplateId: null,
      layouts: null,
      officialLayouts: null,
      template: STANDARD_TEMPLATE,
      loading: false,
      saving: false,
      includeUncertaintyCalculation: false,
    };

    this.changeBreakPoints = this.changeBreakPoints.bind(this);
    this.changeChartReferenceLine = this.changeChartReferenceLine.bind(this);
    this.changeReferenceLineCustomLabel = this.changeReferenceLineCustomLabel.bind(this);
    this.changeReferenceLineCustomLevel = this.changeReferenceLineCustomLevel.bind(this);
    this.changeLayout = this.changeLayout.bind(this);
    this.changeTemplate = this.changeTemplate.bind(this);
    this.changeCompany = this.changeCompany.bind(this);
    this.saveReportOptions = this.saveReportOptions.bind(this);
  }

  static get propTypes() {
    return {
      history: PropTypes.object,
      location: PropTypes.object,
      isSpruceAdmin: PropTypes.bool,
      companyId: PropTypes.string,
      uom: PropTypes.string,
    };
  }

  componentDidMount() {
    const { location = {} } = this.props;
    const companyId = this.props.companyId || (location.state && location.state.companyId);

    if (companyId) {
      this.fetchReportOptions(companyId);
    }
  }

  changeCompany(company) {
    this.fetchReportOptions(company && company.value);
  }

  fetchReportOptions(companyId) {
    this.setState({ loading: true });

    fetchAllVisibleLayouts().then((officialLayouts) =>
      fetchReportOptions(companyId).then((config) =>
        this.setState({
          companyId,
          includeCoverLetter: config.includeCoverLetter,
          coverLetter: config.coverLetter,
          highLevel: config.high,
          lowLevel: config.low,
          chartReferenceLine: config.chartReferenceLine,
          customLabel: config.customLabel,
          customLevel: config.customLevel,
          defaultLayout: deserializeDefaultLayout(config, officialLayouts),
          layoutTemplateId: config.layoutTemplateId,
          template: config.template,
          layouts: config.layouts,
          officialLayouts,
          loading: false,
          includeUncertaintyCalculation: config.includeUncertaintyCalculation,
        }),
      ),
    );
  }

  get actionButtons() {
    const { companyId, saving } = this.state;

    return companyId
      ? [
          <CancelButton key="cancel" onClick={() => this.props.history.goBack()} />,
          <SaveButton disabled={saving} loading={saving} key="save" onClick={this.saveReportOptions} />,
        ]
      : [];
  }

  saveReportOptions() {
    const {
      companyId,
      includeCoverLetter,
      coverLetter,
      chartReferenceLine,
      customLabel,
      customLevel,
      template,
      highLevel: high,
      lowLevel: low,
      defaultLayout,
      includeUncertaintyCalculation,
    } = this.state;
    const { uom } = this.props;

    this.setState({ saving: true });

    return updateReportOptions(companyId, {
      includeCoverLetter,
      coverLetter,
      low,
      high,
      chartReferenceLine,
      customLabel,
      customLevel: customLevel || 0,
      template,
      unitOfMeasure: uom,
      includeUncertaintyCalculation,
      ...serializeDefaultLayout(defaultLayout),
    })
      .then(() => {
        this.setState({ saving: false });
        notifyInfo();
      })
      .catch(() => notifyError());
  }

  changeBreakPoints(lowLevel, highLevel) {
    this.setState({
      lowLevel,
      highLevel,
    });
  }

  changeChartReferenceLine(event) {
    this.setState({
      chartReferenceLine: event.target.value,
    });
  }

  changeReferenceLineCustomLabel(customLabel) {
    this.setState({ customLabel });
  }

  changeReferenceLineCustomLevel(customLevel) {
    this.setState({ customLevel });
  }

  changeTemplate(event) {
    this.setState({
      template: event.target.value,
    });
  }

  changeLayout(value) {
    this.setState({ defaultLayout: value });
  }

  handleUncertaintyToggle = () => {
    this.setState((prevState) => ({
      includeUncertaintyCalculation: !prevState.includeUncertaintyCalculation,
    }));
  };

  render() {
    const { isSpruceAdmin, uom, location } = this.props;
    const {
      defaultLayout,
      officialLayouts,
      lowLevel,
      highLevel,
      template,
      companyId,
      chartReferenceLine,
      customLabel,
      customLevel,
      layouts,
      loading,
    } = this.state;

    return (
      <Page
        header="Report Management"
        actions={this.actionButtons}
        breadCrumbs={getBreadCrumbs('Report Management', location.pathname, [PAGE_PATHS[DASHBOARD]])}
      >
        <Row className="mb-5">
          <If condition={isSpruceAdmin}>
            <Col md={6}>
              <CompanyDropdown value={companyId} onChange={this.changeCompany} />
            </Col>
          </If>
        </Row>
        <Choose>
          <When condition={loading}>
            <Loader />
          </When>
          <When condition={companyId}>
            <Row>
              <Col md={6}>
                <ReportOptions
                  defaultLayout={defaultLayout}
                  options={getDefaultReportLayoutOptions(officialLayouts)}
                  highLevel={highLevel}
                  lowLevel={lowLevel}
                  template={template}
                  uom={uom}
                  changeLayout={this.changeLayout}
                  changeBreakPoints={this.changeBreakPoints}
                  changeTemplate={this.changeTemplate}
                />
                <UncertaintyCalculation
                  isChecked={this.state.includeUncertaintyCalculation}
                  onToggle={this.handleUncertaintyToggle}
                />
              </Col>
              <Col md={6}>
                <ChartReferenceLine
                  value={chartReferenceLine}
                  customLabel={customLabel}
                  customLevel={customLevel}
                  onChangeReferenceLine={this.changeChartReferenceLine}
                  onChangeCustomLabel={this.changeReferenceLineCustomLabel}
                  onChangeCustomLevel={this.changeReferenceLineCustomLevel}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <OfficialReports companyId={companyId} />
              </Col>
            </Row>
            <If condition={layouts}>
              <Row>
                <Col>
                  <ReportLayouts
                    layouts={layouts}
                    companyId={companyId}
                    TemplateOptions={
                      <TemplateOptions
                        highLevel={highLevel}
                        lowLevel={lowLevel}
                        template={template}
                        uom={uom}
                        changeBreakPoints={this.changeBreakPoints}
                        changeTemplate={this.changeTemplate}
                      />
                    }
                  />
                </Col>
              </Row>
            </If>
          </When>
          <When condition={!companyId}>
            <div className="text-center my-5 py-5">No company selected</div>
          </When>
        </Choose>
      </Page>
    );
  }
}

const mapStateToProps = ({ profile }) => ({
  isSpruceAdmin: profile.isSpruceAdmin,
  companyId: profile.companyId,
  uom: profile.uom,
});

export default connect(mapStateToProps)(withEulaRestrictions(withRoleRestrictions(ReportManagementPage, ADMINS)));



// WEBPACK FOOTER //
// src/components/reportManagement/ReportManagementPage.jsx